import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import BackgroundWrapper from "../BackgroundWrapper";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  fetchBoards,
  getBoards,
  getRegistrationPhase,
} from "../../features/tournoi/tournoiSlice";
import type { Tableau } from "../../types";
import HomeClosed from "./HomeClosed";
import HomeCancelled from "./HomeCancelled";
import HomePreOpen from "./HomePreOpen";
import {
  HomeContentSection,
  HomeGallery,
  HomeHighlights,
  HomeReglementCta,
  HomeWhyParticipate,
} from "./HomeMarketingSections";
import { HomeOpenRegistrationHero } from "./HomeOpenRegistrationHero";
import { HomePromo2026 } from "./HomePromo2026";
import { homeHeroColumnSx } from "./homeConstants";

/** Seuil à partir duquel un tableau est affiché comme "bientôt rempli" (ratio places occupées) */
const NEARLY_FULL_RATIO = 0.8;

/**
 * Repli si le tournoi Firestore n’a pas de `dateFinInscriptions` renseigné.
 * En prod, la date affichée vient en principe du document tournoi (saisie admin).
 */
const DEFAULT_DATE_FIN_INSCRIPTIONS = "30 octobre 2026";

function computeBoardStats(boards: Tableau[]): {
  totalEngagements: number;
  totalPlaces: number;
  fillRate: number | null;
  nearlyFullBoards: Array<{ tableau: Tableau; ratio: number }>;
  fullBoards: Tableau[];
  hasFullBoards: boolean;
} {
  const totalEngagements = boards.reduce(
    (sum, b) => sum + (b.nbParticipants ?? 0),
    0
  );
  const totalPlaces = boards.reduce(
    (sum, b) => sum + (b.realNbPlaces ?? 0),
    0
  );
  const fillRate =
    totalPlaces > 0 ? totalEngagements / totalPlaces : null;

  const nearlyFullBoards: Array<{ tableau: Tableau; ratio: number }> = [];
  const fullBoards: Tableau[] = [];
  for (const b of boards) {
    const places = b.realNbPlaces ?? 0;
    if (places <= 0) continue;
    const ratio = (b.nbParticipants ?? 0) / places;
    if (b.isFull ?? ratio >= 1) {
      fullBoards.push(b);
    }
    if (ratio >= NEARLY_FULL_RATIO && ratio < 1) {
      nearlyFullBoards.push({ tableau: b, ratio });
    }
  }
  nearlyFullBoards.sort((a, b) => b.ratio - a.ratio);

  return {
    totalEngagements,
    totalPlaces,
    fillRate,
    nearlyFullBoards,
    fullBoards,
    hasFullBoards: fullBoards.length > 0,
  };
}

const Home: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const registrationPhase = useAppSelector(getRegistrationPhase);
  const tournoi = useAppSelector((state) => state.tournoi.tournoi);
  const boards = useAppSelector(getBoards);

  const isOpenForBoards = registrationPhase === "open";
  const [boardsLoadState, setBoardsLoadState] = useState<
    "idle" | "loading" | "ready"
  >("idle");

  useEffect(() => {
    if (!isOpenForBoards || !tournoi?.id) {
      setBoardsLoadState("idle");
      return;
    }
    let cancelled = false;
    setBoardsLoadState("loading");
    void dispatch(fetchBoards())
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setBoardsLoadState("ready");
      });
    return () => {
      cancelled = true;
    };
  }, [isOpenForBoards, tournoi?.id, dispatch]);

  const stats = useMemo(() => computeBoardStats(boards), [boards]);

  const dateFinInscriptions =
    tournoi?.dateFinInscriptions?.trim() || DEFAULT_DATE_FIN_INSCRIPTIONS;
  const tournamentLabel =
    tournoi?.label?.trim() || "Tournoi national SQY Ping";
  const lieuDisplay =
    tournoi?.lieuCourt?.trim() || tournoi?.adresse?.trim() || null;

  if (
    registrationPhase === "closed" ||
    registrationPhase === "preRegistration"
  ) {
    return <HomePreOpen />;
  }

  if (registrationPhase === "cancelled") {
    return <HomeCancelled />;
  }

  if (registrationPhase === "closedForPlayers") {
    return <HomeClosed />;
  }

  return (
    <BackgroundWrapper>
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1100,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <HomeOpenRegistrationHero
          tournamentLabel={tournamentLabel}
          dateFinInscriptions={dateFinInscriptions}
          lieuDisplay={lieuDisplay}
        />

        {/* ——— CTA inscription + affiche ——— */}
        <Zoom in>
          <Box
            sx={{
              ...homeHeroColumnSx,
              textAlign: "center",
              mb: 2,
              px: 1,
              pt: 0,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="center"
              alignItems="stretch"
              spacing={{ xs: 1.5, sm: 2 }}
              sx={{ flexWrap: "wrap", mb: { xs: 1.25, sm: 1.5 } }}
            >
              <Button
                component={Link}
                to="/register"
                variant="contained"
                color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                size="large"
                sx={{
                  fontWeight: 800,
                  px: { xs: 3, sm: 5 },
                  py: 1.85,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 320 },
                  fontSize: { xs: "1.1rem", sm: "1.15rem" },
                  letterSpacing: "0.01em",
                  ...(theme.palette.mode === "dark"
                    ? {
                        color: "grey.900",
                        bgcolor: "secondary.main",
                        boxShadow: `0 4px 0 rgba(0,0,0,0.35), 0 8px 28px ${alpha(
                          theme.palette.secondary.main,
                          0.55
                        )}`,
                        "&:hover": {
                          bgcolor: "secondary.light",
                          color: "grey.900",
                          boxShadow: `0 4px 0 rgba(0,0,0,0.25), 0 10px 36px ${alpha(
                            theme.palette.secondary.light,
                            0.5
                          )}`,
                        },
                      }
                    : {
                        boxShadow: `0 3px 0 ${alpha(
                          theme.palette.primary.dark,
                          0.35
                        )}, 0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                        "&:hover": {
                          boxShadow: `0 3px 0 ${alpha(
                            theme.palette.primary.dark,
                            0.45
                          )}, 0 10px 28px ${alpha(theme.palette.primary.main, 0.45)}`,
                        },
                      }),
                }}
              >
                Je m&apos;inscris maintenant
              </Button>
              <Button
                component={Link}
                to="/boards"
                variant="outlined"
                color={theme.palette.mode === "dark" ? "inherit" : "secondary"}
                size="medium"
                sx={{
                  fontWeight: 600,
                  width: { xs: "100%", sm: "auto" },
                  alignSelf: { sm: "center" },
                  ...(theme.palette.mode === "dark"
                    ? {
                        borderWidth: 2,
                        borderColor: alpha(theme.palette.common.white, 0.5),
                        color: "grey.100",
                        "&:hover": {
                          borderColor: "common.white",
                          backgroundColor: alpha(
                            theme.palette.common.white,
                            0.08
                          ),
                          color: "common.white",
                        },
                      }
                    : {
                        "&:hover": {
                          borderColor: "secondary.light",
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.08
                          ),
                          color: "secondary.dark",
                        },
                      }),
                }}
              >
                Voir les tableaux
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: { xs: 1.25, sm: 1.75 },
                color:
                  theme.palette.mode === "dark"
                    ? "grey.300"
                    : "text.secondary",
              }}
            >
              Paiement CB sécurisé via Stripe • Confirmation immédiate
            </Typography>
            <HomePromo2026 compact />
          </Box>
        </Zoom>

        {/* ——— Dynamique des inscriptions ——— */}
        <HomeContentSection eyebrow="En direct" title="État des inscriptions">
          {boardsLoadState !== "ready" && stats.totalPlaces === 0 ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Skeleton variant="rounded" height={100} />
                </Grid>
              ))}
            </Grid>
          ) : stats.totalPlaces > 0 ? (
            <Stack spacing={3}>
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.85)"
                      : "text.secondary",
                }}
              >
                {stats.totalEngagements >= 20 ? "Déjà " : ""}
                {stats.totalEngagements} inscription
                {stats.totalEngagements !== 1 ? "s" : ""} pour{" "}
                {stats.totalPlaces} places
                {stats.fillRate != null &&
                  ` (${Math.round(stats.fillRate * 100)} %)`}
                {" — "}
                <Button
                  component={Link}
                  to="/inscrits"
                  variant="text"
                  size="small"
                  sx={{
                    minWidth: 0,
                    p: 0,
                    verticalAlign: "baseline",
                    ...(theme.palette.mode === "dark" && {
                      color: "secondary.main",
                      textDecoration: "underline",
                      "&:hover": {
                        color: "secondary.light",
                        backgroundColor: "transparent",
                      },
                    }),
                  }}
                >
                  Voir la liste des inscrits
                </Button>
              </Typography>

              {stats.nearlyFullBoards.length > 0 && (
                <Box sx={{ ...homeHeroColumnSx, mx: "auto", width: "100%" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.12)
                          : theme.palette.divider,
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(160deg, ${alpha(
                              theme.palette.background.paper,
                              0.92
                            )} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`
                          : theme.palette.background.paper,
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? "0 8px 28px rgba(0,0,0,0.35)"
                          : "0 2px 12px rgba(0,0,0,0.06)",
                      borderRadius: 3,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 1.75 }}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "grey.100"
                                : "text.primary",
                          }}
                        >
                          Tableaux bientôt remplis
                        </Typography>
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.45,
                            borderRadius: 999,
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.warning.light
                                : theme.palette.warning.dark,
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              theme.palette.mode === "dark" ? 0.2 : 0.12
                            ),
                            border: "1px solid",
                            borderColor: alpha(theme.palette.warning.main, 0.35),
                          }}
                        >
                          {stats.nearlyFullBoards.length} en tension
                        </Box>
                      </Stack>
                      <Stack spacing={1.2}>
                        {stats.nearlyFullBoards.map(({ tableau, ratio }) => (
                          <Box
                            key={tableau.id ?? tableau.name}
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor:
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.11)
                                  : alpha(theme.palette.divider, 0.9),
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? alpha(theme.palette.common.white, 0.02)
                                  : alpha(theme.palette.background.default, 0.7),
                            }}
                          >
                            <Stack spacing={0.9}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                                spacing={1}
                              >
                                <Typography variant="body2" fontWeight={600}>
                                  {tableau.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "grey.300"
                                        : "text.secondary",
                                  }}
                                >
                                  {tableau.nbParticipants ?? 0} /{" "}
                                  {tableau.realNbPlaces ?? 0}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, Math.round(ratio * 100))}
                                sx={{
                                  height: 8,
                                  borderRadius: 999,
                                  backgroundColor: alpha(
                                    theme.palette.common.white,
                                    theme.palette.mode === "dark" ? 0.12 : 0.4
                                  ),
                                  "& .MuiLinearProgress-bar": {
                                    borderRadius: 999,
                                    background:
                                      ratio >= 0.9
                                        ? `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`
                                        : `linear-gradient(90deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
                                  },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  textAlign: "right",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "grey.300"
                                      : "text.secondary",
                                }}
                              >
                                {Math.round(ratio * 100)} % rempli
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.85)"
                    : "text.secondary",
              }}
            >
              Aucun tableau n’est encore configuré pour ce tournoi.{" "}
              <Button
                component={Link}
                to="/boards"
                variant="text"
                size="small"
                sx={{
                  minWidth: 0,
                  p: 0,
                  verticalAlign: "baseline",
                  ...(theme.palette.mode === "dark" && {
                    color: "secondary.main",
                    textDecoration: "underline",
                    "&:hover": {
                      color: "secondary.light",
                      backgroundColor: "transparent",
                    },
                  }),
                }}
              >
                Voir les tableaux
              </Button>
            </Typography>
          )}
        </HomeContentSection>

        <HomeGallery />
        <HomeHighlights />
        <HomeWhyParticipate />
        <HomeReglementCta />
      </Box>
    </BackgroundWrapper>
  );
};

export default Home;
